import { useRouter } from 'next/router'
import { useEffect } from 'react'
import { IPage } from '../../types'
import { webToNativeApi } from '../../client/webToNativeApi'
import { dfpUtils, utils } from '../../components/Player/Helpers'
import { ottRoot } from '../../Ott/Api'
import { ILevels } from '../../components/Player/Models/PlayerModels'
import { usePageViewInDay } from './usePageViewInDay'
import { config } from '../../lib'
import { captureImaNavReferrer } from '../../utils/imaReferrer'
import {
  navigationDirectionTracker,
  NavDirection,
} from '../../utils/navigationDirection'

// /**
//  * https://github.com/vercel/next.js/issues/4662
//  * Next.js router doesn't fire on fire page loads when there are no query string params
//  * This fixes it in order for the nativeApp to receive an event anyway
//  **/
let initialRouteTracked = false

const queue: { url: string; shallow: boolean; direction: NavDirection }[] = []

let oldPath = ''
const shouldReportRouteChange = (newPath: string) => {
  const oldPathMatch = oldPath.match(/\/series\/(\d+)/),
    newPathMatch = newPath.match(/\/series\/(\d+)/)
  if (!newPathMatch || !oldPathMatch) {
    return true
  }
  // if (newPathMatch.length > 1 && oldPathMatch.length > 1) {
  //   if (newPathMatch[1] === oldPathMatch[1]) {
  //     return false
  //   }
  // }
  return true
}

/** Notifies the native app about route change events */
export function useNativeAppRouter(
  page: IPage | undefined,
  adTargetEnv: string
) {
  const router = useRouter()
  const pageViewInDay = usePageViewInDay()

  useEffect(() => {
    const routeChangeStart = (
      url: string,
      { shallow }: { shallow: boolean }
    ) => {
      captureImaNavReferrer(window.location.href)
      if (shouldReportRouteChange(url) || !url.includes(ottRoot)) {
        webToNativeApi.routeChangeStart({
          url: url.replace('[[...all]]', ''),
          shallow,
        })
      }
      oldPath = document.location.pathname
    }
    while (queue.length) {
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      const { url, shallow, direction } = queue.pop()!
      if (url !== router.asPath) {
        continue
      }
      triggerRouteChangeComplete({
        url: url.replace('[[...all]]', ''),
        shallow,
        page,
        adTargetEnv,
        pageViewInDay: pageViewInDay.pageViewInDayCount || 1,
        direction,
      })
    }

    const routeChangeComplete = (
      url: string,
      { shallow }: { shallow: boolean }
    ) => {
      // When performing internal navigation, the routeChangeComplete event gets triggered on the
      // old effect, with the old page state, we detect this by comparing the router's path and the
      // event url, when they're equal, this is a 'first load', and we have to trigger the event immediatly
      // otherwise, we push it to a queue, and the event will triggered when the hook is mounted again
      // with the new page state data

      // Read direction as soon as the navigation completes — `history.state`
      // only reliably reflects this destination entry right now, not
      // whenever the queue below eventually gets flushed. Shares the same
      // tracker instance as NavigationProvider, so a back navigation
      // resolved via its beforePopState hook is reflected here too, rather
      // than each maintaining (and colliding over) independent state.
      const direction = navigationDirectionTracker.handleRouteChangeComplete()

      if (router.asPath.replace('[[...all]]', '') === url) {
        triggerRouteChangeComplete({
          url,
          shallow,
          adTargetEnv,
          page,
          pageViewInDay: pageViewInDay.pageViewInDayCount || 1,
          direction,
        })
        return
      }
      queue.push({
        url: url.replace('[[...all]]', ''),
        shallow,
        direction,
      })
    }
    const routeChangeError = (
      error: { cancelled: boolean },
      url: string,
      { shallow }: { shallow: boolean }
    ) => {
      window.dataLayer.push({
        event: 'routeChangeError',
        url,
        cancelled: error.cancelled
          ? 'Navigation cancelled'
          : 'Navigation failed',
      })
      webToNativeApi.routeChangeError({
        error,
        url,
        shallow,
      })
    }
    router.events.on('routeChangeStart', routeChangeStart)
    router.events.on('routeChangeComplete', routeChangeComplete)
    router.events.on('routeChangeError', routeChangeError)

    if (!initialRouteTracked && !router.asPath.includes('[[...all]]')) {
      initialRouteTracked = true
      routeChangeStart(router.asPath, {
        shallow: true,
      })
      routeChangeComplete(router.asPath, {
        shallow: true,
      })
    }

    return () => {
      router.events.off('routeChangeStart', routeChangeStart)
      router.events.off('routeChangeComplete', routeChangeComplete)
      router.events.off('routeChangeError', routeChangeError)
    }
  }, [
    adTargetEnv,
    page,
    pageViewInDay,
    router.asPath,
    router.events,
    router.pathname,
  ])
}

const triggerRouteChangeComplete = ({
  url,
  shallow,
  page,
  adTargetEnv,
  pageViewInDay,
  direction,
}: {
  url: string
  shallow: boolean
  page: IPage | undefined
  adTargetEnv: string
  pageViewInDay: number
  direction: NavDirection
}) => {
  let shouldShowMaavaron = true,
    interstitial_page_interval: number

  if (page?.PageMeta) {
    shouldShowMaavaron = !!page?.PageMeta?.ads?.block?.shouldShowMaavaron
  }
  if (page?.SiteMeta) {
    interstitial_page_interval = page.SiteMeta.config?.maavaron?.app || 1
  } else {
    interstitial_page_interval =
      config.configFile.interstitial_page_interval || 1
  }

  shouldShowMaavaron =
    shouldShowMaavaron &&
    direction !== 'back' &&
    shouldShowAppInterstitial.get(interstitial_page_interval)

  const levels = (page?.PageMeta?.levels || {}) as ILevels
  if (!levels.level_0_eng) {
    const tempLevels = url.split('/').filter(p => !!p)
    tempLevels.map((l, i) => {
      levels[`level_${i + 1}_eng` as keyof ILevels] = l
      levels[`level_${i + 1}_heb` as keyof ILevels] = l
    })
  }
  let title = page?.SEO?.title
  if (!title) {
    setTimeout(() => {
      title = document.title
      if (shouldReportRouteChange(url) || !url.includes(ottRoot)) {
        webToNativeApi.routeChangeComplete({
          url: url,
          shallow: shallow,
          title: title,
          pageType: url.includes(ottRoot)
            ? 'gridvod'
            : page?.PageMeta?.pageType,
          applicationPageType: url.includes(ottRoot)
            ? 'vod'
            : page?.PageMeta?.applicationPageType,
          custParams: dfpUtils.buildCustParams({
            pageType: page?.PageMeta?.pageType,
            levels,
            route: url,
            tags: page?.Content?.Item?.tags,
            adTargetEnv,
            pageViewInDay,
          }),
          shouldShowMaavaron,
        })
      }
    }, 200)
  } else {
    webToNativeApi.routeChangeComplete({
      url: url,
      shallow: shallow,
      title: title,
      pageType: url.includes(ottRoot) ? 'gridvod' : page?.PageMeta?.pageType,
      applicationPageType: url.includes(ottRoot)
        ? 'vod'
        : page?.PageMeta?.applicationPageType,
      custParams: dfpUtils.buildCustParams({
        pageType: page?.PageMeta?.pageType,
        levels,
        route: url,
        tags: page?.Content?.Item?.tags,
        adTargetEnv,
        pageViewInDay,
      }),
      shouldShowMaavaron:
        shouldShowMaavaron &&
        shouldShowAppInterstitial.get(
          page?.SiteMeta.config?.maavaron?.app || 3
        ),
    })
  }
}

const shouldShowAppInterstitial = {
  get: (intervals: number) =>
    (utils.getPageViewedInSession() - 1) % intervals === 0,
}
